/**
 * @module routes.Sessions.AllCoachesPublicSession._components.useFacilityPrograms
 */
import { useEffect, useState } from "react";
import { DateTime } from "luxon";

import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";

export interface FacilityProgram {
  id: number;
  title: string;
  start: DateTime;
  end: DateTime;
  /**
   * Every id the payload offers for the coaches running it — the private surface
   * carries them, the public one does not.
   */
  coachIds: string[];
  /**
   * Their names, lowercased. The public payload identifies a programme's coaches
   * by name only — `{ first_name, last_name, full_name, profile_picture }`, no id
   * of any kind — so a grid column can only be matched by name there.
   */
  coachNames: string[];
}

/**
 * The facility's programmes on a given day — the clinics and events the frame
 * draws as blocks in a coach's column.
 *
 * Only programmes. A coach's time can be taken by a booked lesson or an
 * unavailability too, and neither draws anything: the cell simply stays blank, as
 * it does for any hour a coach is not offering.
 */
const useFacilityPrograms = (
  facilityId: string | undefined,
  date: string | null,
  isPublicPage = true,
): FacilityProgram[] => {
  const [programs, setPrograms] = useState<FacilityProgram[]>([]);

  useEffect(() => {
    if (!facilityId || !date) {
      setPrograms([]);
      return undefined;
    }

    let active = true;

    // `from`/`to` ask for the occurrences overlapping this day. Without them the
    // index clamps a recurrence to its first occurrence, so a clinic recurring
    // daily answered for its first day and no other (coaching_service#868).
    const dayStart = DateTime.fromISO(date).startOf("day");
    const dayEnd = DateTime.fromISO(date).endOf("day");

    // In the app, ask the private surface at version 3. Two reasons, both from
    // the endpoint itself: the public one keys on the facility's *uuid* with
    // public_programs enabled — a numeric id 404s, which is silent here — and
    // versions below 3 drop recurring clinics outright, which is most of what a
    // facility calendar wants to show.
    //
    // Deliberately unfiltered by registration status. Asking for
    // `open_now_or_in_future`, as the coach page does, hid programmes whose
    // registration window is not open yet and left the coach's day looking free
    // when it is not.
    schedulingFetchGet(
      "/programs",
      {
        ...(isPublicPage ? { facility_id: facilityId } : {}),
        from: dayStart.toISO(),
        to: dayEnd.toISO(),
      },
      isPublicPage ? {} : { version: 3 },
      undefined,
      isPublicPage,
    )
      .then((response: any) => {
        if (!active) {
          return;
        }

        const rows = response?.data?.active || response?.data || [];
        const onThisDay = (Array.isArray(rows) ? rows : [])
          .map((program: any): FacilityProgram | null => {
            const zone = program.timeZone || program.time_zone;
            // A programme's times live on its event, not on the programme row, so
            // the payload may surface them either way round.
            const startRaw =
              program.startTime ||
              program.start_time ||
              program.start ||
              program.event?.startTime ||
              program.events?.[0]?.startTime;
            const endRaw =
              program.endTime ||
              program.end_time ||
              program.event?.endTime ||
              program.events?.[0]?.endTime;

            const start = DateTime.fromISO(startRaw, zone ? { zone } : {});
            const end = DateTime.fromISO(endRaw, zone ? { zone } : {});

            // The window above does this server-side; this keeps the screen
            // honest against a deployment that has not got that change yet, when
            // the index answers with first occurrences regardless.
            if (!start.isValid || start.toISODate() !== date) {
              return null;
            }

            // The private surface answers with everything the athlete can see,
            // since its filters cover coach, tag, category and name but not the
            // facility.
            const programFacility = String(
              program.facilityId ?? program.facility_id ?? "",
            );
            if (
              !isPublicPage &&
              facilityId &&
              programFacility &&
              programFacility !== String(facilityId)
            ) {
              return null;
            }

            const coachList = program.coaches || [];

            const coachIds = [
              ...coachList.flatMap((coach: any) => [
                coach?.id,
                coach?.externalId,
                coach?.coachProfileId,
                coach?.coach_profile_id,
                coach?.calendarUuid,
              ]),
              ...(program.coachProfileIds || []),
              ...(program.coach_profile_ids || []),
            ]
              .filter(Boolean)
              .map(String);

            const coachNames = [
              ...coachList.map(
                (coach: any) =>
                  coach?.full_name ||
                  coach?.fullName ||
                  [coach?.first_name ?? coach?.firstName, coach?.last_name ?? coach?.lastName]
                    .filter(Boolean)
                    .join(" "),
              ),
              ...(program.coaches_full_names || program.coachesFullNames || []),
            ]
              .filter(Boolean)
              .map((name: string) => String(name).trim().toLowerCase());

            return {
              id: program.id,
              title: program.title,
              start,
              end: end.isValid ? end : start.plus({ hours: 1 }),
              coachIds,
              coachNames,
            };
          })
          .filter(Boolean) as FacilityProgram[];

        setPrograms(onThisDay);
      })
      .catch(() => {
        if (active) {
          setPrograms([]);
        }
      });

    return () => {
      active = false;
    };
  }, [facilityId, date, isPublicPage]);

  return programs;
};

export default useFacilityPrograms;
